/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Flex, Box, Text, Center, Image, Spinner } from '@chakra-ui/core';
import { MdPersonSearch } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import _isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import { SortingRule } from 'react-table';
import { Header } from '../../app/topBar/Header';
import colors from '../../styles/colors';
import CandidateSearchTableView from './CandidateSearchTableView';
import { CandidateSearchResponseType } from './CandidateSearchTypes';
import { useStoreActions, useStoreState } from '../../models/hooks';
import { CandidateSearch, SearchCriteria } from './candidateSearch/CandidateSearch';
import { CandidateFilter, Filters } from './candidateFilter/CandidateFilter';

const CANDIDATE_SEARCH_DISABLE_SORT_RESET = true;

type SortOrder = 'asc' | 'desc';
type SortBy = 'fullName' | 'locationName' | 'statusDetails.status.keyword' | 'statusDetails.lastUpdatedAt';

/**
 * @hook useCandidateSearch
 * @description A custom hook to encapsulate candidate search logic, including data fetching,
 * state management, and pagination.
 * @param {SearchCriteria} search - The search criteria from the search input fields.
 * @param {Filters} filters - The filter criteria from the filter component.
 */
const useCandidateSearch = (
  search: SearchCriteria,
  filters: Filters,
  sortBy: SortBy | undefined,
  sortOrder: SortOrder | undefined,
  hasSearchCriteria: boolean,
) => {
  const { t } = useTranslation('candidateSearch');
  const accountId = useStoreState((state) => state.app.accounts?.id);
  const { searchCandidates } = useStoreActions((actions) => actions.candidateSearch);

  const [candidatesList, setCandidatesList] = useState<CandidateSearchResponseType[]>([]);
  const [hasData, setHasData] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(20);
  const [totalDataCount, setTotalDataCount] = useState<number>(0);

  const loadSearchCandidates = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    async (/* istanbul ignore next */ page: number = 1, limit?: number) => {
      // istanbul ignore next
      if (!hasSearchCriteria) {
        setHasData(false);
        setCandidatesList([]);
        setCurrentPage(1);
        setTotalDataCount(0);
        return;
      }

      setIsFetching(true);
      setIsLoading(page === 1);

      interface CandidateSearchPayload {
        page: number;
        limit: number;
        seekerId: null;
        fullName: string | null;
        email: string | null;
        phone: string | null;
        requisitionId: string | null;
        seekerHireDetailId: string | null;
        status: string[] | null;
        positionNames: string[] | null;
        locationNames: string[] | null;
        sortBy?: SortBy;
        sortOrder?: SortOrder;
      }
      const searchPayload: CandidateSearchPayload = {
        page: page ?? /* istanbul ignore next */ 1,
        limit: limit ?? pageLimit,
        seekerId: null,
        fullName: !_isEmpty(search.candidateName) ? search.candidateName : /* istanbul ignore next */ null,
        email: !_isEmpty(search.email) ? search.email : null,
        phone: !_isEmpty(search.phone) ? search.phone : null,
        requisitionId: !_isEmpty(search.requisitionId) ? search.requisitionId : null,
        seekerHireDetailId: !_isEmpty(search.hireId) ? search.hireId : null,
        status: !_isEmpty(filters.status) ? filters.status : null,
        positionNames: !_isEmpty(filters.positions) ? /* istanbul ignore next */ filters.positions : null,
        locationNames: !_isEmpty(filters.locations) ? /* istanbul ignore next */ filters.locations : null,
      };

      // istanbul ignore else
      if (sortBy) {
        searchPayload.sortBy = sortBy;
        searchPayload.sortOrder = sortOrder;
      }

      // istanbul ignore else
      if (accountId) {
        try {
          const response = await searchCandidates({
            accountId,
            searchData: searchPayload,
          });
          setCandidatesList(response?.data || /* istanbul ignore next */ []);
          setCurrentPage(response?.page || /* istanbul ignore next */ 1);
          setTotalDataCount(response?.total || 0);
          setHasData(true);
        } catch (error) {
          console.error(t('error.search'), error);
          setHasData(false);
        } finally {
          setIsFetching(false);
          setIsLoading(false);
        }
      } else {
        setIsFetching(false);
      }
    },
    [accountId, hasSearchCriteria, search, filters, searchCandidates, pageLimit, t, sortBy, sortOrder],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((p: number) => {
        loadSearchCandidates(p, 20);
        setPageLimit(20);
      }, 300),
    [loadSearchCandidates, setPageLimit],
  );

  useEffect(() => {
    if (hasSearchCriteria) {
      debouncedSearch(1);
    }
    return () => debouncedSearch.cancel();
    // eslint-disable-next-line
  }, [hasSearchCriteria, search, filters, sortBy, sortOrder]);

  const handlePageChange = useCallback(
    (page: number) => {
      /* istanbul ignore if */
      if (page === currentPage) return;
      loadSearchCandidates(page);
    },
    [loadSearchCandidates, currentPage],
  );

  const handleItemsPerPageChange = useCallback(
    (numItems: number) => {
      /* istanbul ignore if */
      if (numItems === pageLimit) return;
      setPageLimit(numItems);
      setCurrentPage(1); // Reset to first page
      loadSearchCandidates(1, numItems);
    },
    [loadSearchCandidates, pageLimit],
  );

  return {
    candidatesList,
    hasData,
    isLoading,
    isFetching,
    currentPage,
    pageLimit,
    totalDataCount,
    handlePageChange,
    handleItemsPerPageChange,
    resetCandidatesListAndPagination: () => {
      setCandidatesList([]);
      setCurrentPage(1);
      setTotalDataCount(0);
      setHasData(false);
    },
  };
};

/**
 * @component WelcomeView
 * @description Renders the initial view prompting the user to start a search.
 */

const WelcomeView = () => {
  const { t } = useTranslation('candidateSearch');
  return (
    <Box position="relative" mt={20} data-testid="welcome-view">
      <Flex direction="column" justifyContent="center" alignItems="center" textAlign="center" py={5} width="370px" mx="auto">
        <Center>
          <MdPersonSearch size={100} color={colors.gray[400]} />
        </Center>
        <Center>
          <Text fontSize="24px" fontWeight={700}>
            {t('exploreProfiles')}
          </Text>
        </Center>
        <Box mt={2}>
          <Text fontSize="15px" fontWeight={400} color={colors.gray[300]}>
            {t('enterDetailsText')}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};

/**
 * @component NoResultsView
 * @description Renders when a search is performed but yields no results.
 */
const NoResultsView = () => {
  const { t } = useTranslation('candidateSearch');
  return (
    <Box position="relative" mt={20}>
      <Flex direction="column" justifyContent="center" alignItems="center" textAlign="center" py={5} width="370px" mx="auto">
        <Center>
          <Image src="/seaching_icon.png" alt={t('noMatchFound')} />
        </Center>
        <Center>
          <Text fontSize="24px" fontWeight={700} data-testid="noMatchFound">
            {t('noMatchFound')}
          </Text>
        </Center>
        <Box mt={2}>
          <Text fontSize="15px" fontWeight={400} color={colors.gray[300]} data-testid="noMatchFoundText">
            {t('noMatchFoundText')}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};

const defaultSearch: SearchCriteria = {
  candidateName: '',
  phone: '',
  email: '',
  hireId: '',
  requisitionId: '',
};

const defaultFilters: Filters = {
  locations: [],
  selectAllLocations: true,
  status: [],
  selectAllStatus: true,
  positions: [],
  selectAllPositions: true,
};

const defaultSortBy: SortBy = 'statusDetails.lastUpdatedAt';
const defaultSortOrder: SortOrder = 'desc';
/**
 * @component CandidateSearchView
 * @description The main view for searching candidates. It orchestrates the search inputs,
 * filters, and results display.
 */
export const CandidateSearchView = (): JSX.Element => {
  const { t } = useTranslation('candidateSearch');
  const isHireFormEnabled = useStoreState((s) => s.app.accounts?.configuration?.hireFormEnabled);

  const [search, setSearch] = useState<SearchCriteria>(defaultSearch);
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [sortBy, setSortBy] = useState<SortBy | undefined>(defaultSortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder | undefined>(defaultSortOrder);

  const hasSearchCriteria = useMemo(() => Object.values(search).some((val) => !_isEmpty(val)), [search]);

  const {
    candidatesList,
    hasData,
    isLoading,
    isFetching,
    currentPage,
    pageLimit,
    totalDataCount,
    handlePageChange,
    handleItemsPerPageChange,
    resetCandidatesListAndPagination,
  } = useCandidateSearch(search, filters, sortBy, sortOrder, hasSearchCriteria);

  const handleSort = useCallback(
    (columnId: SortBy) => {
      if (!hasSearchCriteria) {
        return;
      }

      if (sortBy === columnId) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(columnId);
        setSortOrder('asc');
      }
    },
    [hasSearchCriteria, sortBy, sortOrder],
  );

  const handleReset = useCallback(() => {
    setSearch(defaultSearch);
    setFilters(defaultFilters);
    setSortBy(defaultSortBy);
    setSortOrder(defaultSortOrder);
    resetCandidatesListAndPagination();
    // The useCandidateSearch hook manages candidatesList and hasData based on search and filters.
    // So, no direct manipulation of candidatesList or hasData is needed here.
  }, [setSearch, setFilters, resetCandidatesListAndPagination]);

  const tableSortBy = useMemo<SortingRule<CandidateSearchResponseType>[]>(() => {
    // istanbul ignore next
    if (!sortBy) return [];
    return [{ id: sortBy, desc: sortOrder === 'desc' }];
  }, [sortBy, sortOrder]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Center mt={20} data-testid="loading-candidates">
          <Spinner size="xl" />
        </Center>
      );
    }
    if (!hasData) {
      return <WelcomeView />;
    }
    if (candidatesList.length > 0) {
      return (
        <CandidateSearchTableView
          data={candidatesList}
          isLoading={isLoading}
          currentPage={currentPage}
          totalDataCount={totalDataCount}
          handlePageChange={handlePageChange}
          pageLimit={pageLimit}
          handleItemsPerPageChange={handleItemsPerPageChange}
          isFetching={isFetching}
          // storageKey="candidateLookupTable"
          isHireFormEnabled={isHireFormEnabled}
          sortBy={tableSortBy}
          onSort={handleSort}
          disableSortReset={CANDIDATE_SEARCH_DISABLE_SORT_RESET}
        />
      );
    }
    return <NoResultsView />;
  };

  return (
    <Box height="100vh" overflow="hidden">
      <Header showMenu showPageTitle={t('searchTitle')} />
      <Box p={5} pt={2} height="calc(100vh - 80px)" display="flex" flexDirection="column">
        <Box position="sticky" top={0} bg={colors.white} zIndex={8} pb={4}>
          <CandidateSearch search={search} setSearch={setSearch} onReset={handleReset} isFetching={isFetching} />
          <CandidateFilter filters={filters} onChange={setFilters} isFetching={isFetching} />
        </Box>

        <Box flex="1" overflowY="auto">
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};
